import { BigNumberExt } from "$root/third-party-integrations/bigNumber";
import BigNumber from "bignumber.js";

/**
 * Decimal places to which every arithmetic result of this module is rounded.
 *
 * JavaScript numbers are IEEE-754 doubles, which only carry ~15–17 significant
 * decimal digits. Chained native arithmetic (`+ - * /`) therefore accumulates
 * round-off noise in the trailing digits — the classic `0.1 + 0.2 === 0.30000000000000004`.
 * Capping every computed value at 14 decimal places strips that noise while keeping all
 * meaningful precision for weights (0–100 %), ratios and monetary amounts.
 *
 * This is deliberately *finer* than the domain display roundings
 * (`WEIGHT_PRECISION = 4`, `NORMALIZE_PRECISION = 2`): it only removes float artifacts,
 * never real precision, so it composes safely with — and never overrides — those roundings.
 */
export const LAB_MATH_PRECISION = 14;

/**
 * Significant decimal digits an IEEE-754 double reliably round-trips (15). Noise-stripping
 * before a truncation must be RELATIVE to magnitude, not a fixed decimal-place count:
 * monetary values reach $10T (`MAX_LAB_AUM`), where the double ULP is ~0.002 and an
 * absolute strip like {@link LAB_MATH_PRECISION} (14 dp) sits far below the noise floor,
 * stripping nothing.
 */
const FLOAT_SIGNIFICANT_DIGITS = 15;

/**
 * Truncates `value` to `precision` decimal places (`ROUND_DOWN`, toward zero) after first
 * stripping IEEE-754 noise at {@link FLOAT_SIGNIFICANT_DIGITS} significant digits — a value
 * stored as 8.999999999999998 is "really" 9.00 and must not truncate to 8.99. Use for the
 * totals displays, which must never show a fully-allocated 100 % / AUM when the underlying
 * total is still short of it.
 */
export function truncateToPrecision(value: number, precision: number): number {
	if (!Number.isFinite(value)) {
		return value;
	}
	return BigNumber(value)
		.precision(FLOAT_SIGNIFICANT_DIGITS, BigNumber.ROUND_HALF_UP)
		.dp(precision, BigNumber.ROUND_DOWN)
		.toNumber();
}

/** Precise `a + b`, rounded to {@link LAB_MATH_PRECISION}. */
export function labAdd(a: number, b: number): number {
	return BigNumberExt.roundUp(BigNumber(a).plus(b), LAB_MATH_PRECISION);
}

/** Precise `a - b`, rounded to {@link LAB_MATH_PRECISION}. */
export function labSub(a: number, b: number): number {
	return BigNumberExt.roundUp(BigNumber(a).minus(b), LAB_MATH_PRECISION);
}

/** Precise `a * b`, rounded to {@link LAB_MATH_PRECISION}. */
export function labMul(a: number, b: number): number {
	return BigNumberExt.roundUp(BigNumber(a).times(b), LAB_MATH_PRECISION);
}

/**
 * Precise `a / b`, rounded to {@link LAB_MATH_PRECISION}.
 * Callers must guard against `b === 0` (BigNumber yields `±Infinity`, which passes through).
 */
export function labDiv(a: number, b: number): number {
	return BigNumberExt.roundUp(BigNumber(a).div(b), LAB_MATH_PRECISION);
}

/**
 * Precise sum of an array, accumulated in BigNumber and rounded to
 * {@link LAB_MATH_PRECISION}. Use instead of `sumArrayLike` for any weight/monetary total
 * that feeds further calculation or display.
 */
export function labSum<T>(arr: readonly T[], extract: (value: T) => number): number {
	let sum = BigNumber(0);
	for (const value of arr) {
		sum = sum.plus(extract(value));
	}
	return BigNumberExt.roundUp(sum, LAB_MATH_PRECISION);
}

/**
 * Finest decimal step (in percent) a double reliably keeps distinct for values up to 100: the
 * double spacing near 100 is ≈ 1.42·10⁻¹⁴, so a 14th decimal place collapses neighbours
 * (`99.99999999999999 === 99.99999999999998`) while 10⁻¹³ always survives. Ceiling for every
 * decimal-places setting that drives a comparison against a percentage.
 */
export const MAX_PERCENT_DECIMAL_PLACES = 13;

/**
 * Sum of an array, accumulated in BigNumber and returned without rounding. Use for a total that
 * is compared with {@link compareAtPrecision}: rounding it first ({@link labSum}) rounds it twice
 * and can flip a value sitting on the half-way point.
 */
export function exactSum<T>(arr: readonly T[], extract: (value: T) => number): number {
	let sum = BigNumber(0);
	for (const value of arr) {
		sum = sum.plus(extract(value));
	}
	return sum.toNumber();
}

/**
 * Compares `value`, rounded half-up to `decimalPlaces`, with `target`: `1` above, `0` equal,
 * `-1` below, `null` when `value` is `NaN`. Half-up is the rounding `formatNumber` displays
 * with, so a value this reports as equal is one the display shows as `target`.
 */
export function compareAtPrecision(value: number, target: number, decimalPlaces: number): -1 | 0 | 1 | null {
	return BigNumber(value).dp(decimalPlaces, BigNumber.ROUND_HALF_UP).comparedTo(target);
}

/**
 * Rounds `value` to `decimalPlaces` away from `target` — up when above it, down when below — and
 * at least one step (`10^-decimalPlaces`) off it, so the result is on the same side of `target`
 * as `value` and never equals it. `value === target` returns `target`. IEEE-754 noise is
 * stripped at {@link FLOAT_SIGNIFICANT_DIGITS} first, as in {@link truncateToPrecision}, so a
 * stored `100.01000000000001` rounds to `100.01`, not `100.02`.
 */
export function roundAwayFrom(value: number, target: number, decimalPlaces: number): number {
	if (value === target || !Number.isFinite(value)) {
		return value;
	}
	const step = BigNumber(10).pow(-decimalPlaces);
	const denoised = BigNumber(value).precision(FLOAT_SIGNIFICANT_DIGITS, BigNumber.ROUND_HALF_UP);
	if (value > target) {
		return BigNumber.max(denoised.dp(decimalPlaces, BigNumber.ROUND_CEIL), BigNumber(target).plus(step)).toNumber();
	}
	return BigNumber.min(denoised.dp(decimalPlaces, BigNumber.ROUND_FLOOR), BigNumber(target).minus(step)).toNumber();
}
